import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, Star, Flame, ArrowRight } from 'lucide-react';
import { DjayIdentity } from '../../../mascot';
import { type CelebrationTriggers } from '../../hooks/useSessionExperience';
import { DjayGrowthState } from '../../../components/shared/DjayGrowthState';
import { FAMILY_DISPLAY_NAMES } from '../../../content/familyLabels';
import { hapticManager } from '../../../utils/hapticManager';
import { SummaryShell } from './SummaryShell';
interface CelebrationMomentsProps {
  triggers: CelebrationTriggers;
  onComplete: () => void;
}
type MomentType = 'foutloos' | 'record' | 'stageUp' | 'streak';
export function CelebrationMoments({
  triggers,
  onComplete
}: CelebrationMomentsProps) {
  const [moments, setMoments] = useState<MomentType[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  useEffect(() => {
    const queue: MomentType[] = [];
    if (triggers.foutloos) queue.push('foutloos');
    if (triggers.recordBroken) queue.push('record');
    if (triggers.stageUpgrades.length > 0) queue.push('stageUp');
    if (triggers.streakMilestone) queue.push('streak');
    setMoments(queue.slice(0, 3)); // hard cap
  }, [triggers]);
  useEffect(() => {
    if (moments.length === 0) {
      onComplete();
      return;
    }
    if (currentIndex >= moments.length) {
      onComplete();
      return;
    }
    const timer = setTimeout(() => {
      setCurrentIndex(prev => prev + 1);
    }, 1800);
    return () => clearTimeout(timer);
  }, [currentIndex, moments, onComplete]);
  const handleSkip = () => {
    hapticManager.hapticSelection();
    onComplete();
  };
  if (moments.length === 0 || currentIndex >= moments.length) return null;
  const currentMoment = moments[currentIndex];
  return <SummaryShell tone="celebrated" cornerSlot={<button type="button" onClick={handleSkip} aria-label="Door naar overzicht" className="flex h-11 w-11 items-center justify-center rounded-full bg-white/90 text-slate-600 shadow-md backdrop-blur transition-transform hover:scale-105 active:scale-95" data-id="element-12779" data-mp="1ln149dqt348q">
        
          <ArrowRight className="h-5 w-5" data-id="element-12780" data-mp="3fy0t1kl67dxu" />
        </button>} data-id="element-12778" data-mp="1lpdl1nktv8k7">
      
      {/* Whole-area click to skip (excluding the corner slot) */}
      <button type="button" onClick={handleSkip} aria-label="Tik om verder te gaan" className="absolute inset-0 cursor-pointer" data-id="element-12781" data-mp="31604bqogpcpo" />
      

      <div className="relative flex flex-col items-center text-center" data-id="element-12782" data-mp="3tw8plreaod8h">
        {/* Progress dots */}
        <div className="mb-8 flex gap-1.5" data-id="element-12783" data-mp="2qz4oe55qp5q">
          {moments.map((_, idx) => <div key={idx} className={`h-1.5 rounded-full transition-all ${idx <= currentIndex ? 'w-8 bg-amber-400' : 'w-6 bg-slate-200'}`} data-id="element-12784" data-mp="2o34ej5u7oe4" data-mpi={idx} />)}
        </div>

        <AnimatePresence mode="wait" data-id="element-12785" data-mp="3owcihjaaw5op">
          <motion.div key={currentMoment} initial={{
          opacity: 0,
          scale: 0.95,
          y: 10
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 1.02,
          y: -10
        }} transition={{
          duration: 0.3
        }} className="flex flex-col items-center" data-id="element-12786" data-mp="3ozibh9m1vrpp">
            
            {currentMoment === 'foutloos' && <>
                <DjayIdentity emotion="cheering" size="lg" className="mb-6" data-id="element-12787" data-mp="2fxfaiz19oe9w" />
                <div className="mb-5 flex gap-1.5" data-id="element-12788" data-mp="2ayc4bb9jw20c">
                  {Array.from({
                length: 5
              }).map((_, i) => <motion.div key={i} initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} transition={{
                delay: i * 0.08,
                type: 'spring'
              }} className="h-5 w-5 rounded-full bg-emerald-500 ring-4 ring-emerald-200" data-id="element-12789" data-mp="1n9jfrzv9cwfr" data-mpi={i} />)}
                </div>
                <h2 className="text-3xl font-bold text-slate-800" data-id="element-12790" data-mp="1s69v2kz8no04">Foutloos!</h2>
                <p className="mt-1.5 text-base text-slate-600" data-id="element-12791" data-mp="14gp7fos542ki">
                  Alles in één keer goed.
                </p>
              </>}

            {currentMoment === 'record' && <>
                <DjayIdentity emotion="celebrating" size="lg" className="mb-6" data-id="element-12792" data-mp="2stp4z98dvfm" />
                <motion.div initial={{
              scale: 0,
              rotate: -10
            }} animate={{
              scale: 1,
              rotate: 0
            }} transition={{
              type: 'spring',
              bounce: 0.5
            }} className="mb-5 rounded-full bg-amber-100 p-5" data-id="element-12793" data-mp="6scb7ycsgck1">
                
                  <Trophy className="h-14 w-14 text-amber-500" data-id="element-12794" data-mp="lhi6jb8i9imn" />
                </motion.div>
                <h2 className="text-3xl font-bold text-slate-800" data-id="element-12795" data-mp="qfs0xjmeof77">
                  Nieuw record!
                </h2>
                <p className="mt-1.5 text-base text-slate-600" data-id="element-12796" data-mp="ujhs8gugtavx">
                  Sneller of beter dan ooit.
                </p>
              </>}

            {currentMoment === 'stageUp' && triggers.stageUpgrades[0] !== undefined && <>
                  <DjayIdentity emotion="proud" size="lg" className="mb-6" data-id="element-12797" data-mp="318mj7jaecao9" />
                  <div className="mb-5 flex items-center gap-3" data-id="element-12798" data-mp="31az7sfantjaa">
                    <DjayGrowthState level={triggers.stageUpgrades[0].oldStage.level} size="lg" className="opacity-50 grayscale" data-id="element-12799" data-mp="2tr7tz25c8028" />
                
                    <motion.div initial={{
                x: -10,
                opacity: 0
              }} animate={{
                x: 0,
                opacity: 1
              }} transition={{
                delay: 0.3
              }} data-id="element-12800" data-mp="2jzk95k3adh9q">
                  
                      <Star className="h-7 w-7 text-emerald-400" data-id="element-12801" data-mp="3cmes2ckgg8l3" />
                    </motion.div>
                    <motion.div initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} transition={{
                delay: 0.5,
                type: 'spring'
              }} data-id="element-12802" data-mp="37nblgnottub0">
                  
                      <DjayGrowthState level={triggers.stageUpgrades[0].newStage.level} size="lg" data-id="element-12803" data-mp="1rbeoexc8fs9w" />
                  
                    </motion.div>
                  </div>
                  <h2 className="text-3xl font-bold text-slate-800" data-id="element-12804" data-mp="1oqqev2f2gb8r">
                    Niveau omhoog!
                  </h2>
                  <p className="mt-1.5 text-base text-slate-600" data-id="element-12805" data-mp="1tq34fuxbnwf1">
                    {FAMILY_DISPLAY_NAMES[triggers.stageUpgrades[0].familyId] ?? 'Deze vragen'}{' '}
                    gaan steeds beter.
                  </p>
                </>}

            {currentMoment === 'streak' && <>
                <DjayIdentity emotion="celebrating" size="lg" className="mb-6" data-id="element-12806" data-mp="15zyz9j4hjolx" />
                <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1.1
            }} transition={{
              type: 'spring',
              bounce: 0.6
            }} className="mb-5 rounded-full bg-orange-100 p-5" data-id="element-12807" data-mp="1aqp4mwcwvrgm">
                
                  <Flame className="h-14 w-14 text-orange-500" data-id="element-12808" data-mp="10yt4lfr9lr9r" />
                </motion.div>
                <h2 className="text-3xl font-bold text-slate-800" data-id="element-12809" data-mp="n3h9h7975ysb">
                  {triggers.streakMilestone} dagen!
                </h2>
                <p className="mt-1.5 text-base text-slate-600" data-id="element-12810" data-mp="8e5xqsh40hxd">
                  Wat een doorzettingsvermogen.
                </p>
              </>}
          </motion.div>
        </AnimatePresence>

        <p className="mt-10 text-xs text-slate-400" data-id="element-12811" data-mp="hfvmtrnir4rz">Tik om verder te gaan</p>
      </div>
    </SummaryShell>;
}